// Phone navigation: bottom tab bar + "More" sheet. Both only switch the
// workspace's UI category; they navigate nowhere and list no launch targets.
import React, { useEffect, useRef } from "react";
import { Ellipsis } from "lucide-react";
import { CATEGORY } from "../workspaceCategories";

export const MORE_CATEGORY_IDS = [CATEGORY.PAGES, CATEGORY.ADMIN, CATEGORY.FAVOURITES];

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export const MoreSheet = ({ open, entries, active, onSelect, onClose, returnFocusRef }) => {
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    sheetRef.current?.querySelector("button")?.focus();
    const opener = returnFocusRef?.current;
    return () => opener?.focus?.();
  }, [open, returnFocusRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !sheetRef.current) return;
      const focusable = sheetRef.current.querySelectorAll(FOCUSABLE);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div className="cc-menu-backdrop cc-more-backdrop" aria-hidden="true" onClick={onClose} />
      <div ref={sheetRef} className="cc-more-sheet" role="dialog" aria-modal="true" aria-label="More">
        <div className="cc-menu__grab" aria-hidden="true" />
        <h2 className="cc-more-sheet__title">More</h2>
        {entries.map(({ id, label, icon: Icon, count }) => (
          <button
            key={id}
            type="button"
            className="cc-menu__item"
            aria-current={active === id ? "page" : undefined}
            onClick={() => onSelect(id)}
          >
            <Icon aria-hidden="true" />
            {label}
            <span className="cc-menu__count">{count}</span>
          </button>
        ))}
      </div>
    </>
  );
};

const MobileBottomNav = ({ tabs, active, moreOpen, moreActive, onSelect, onToggleMore, moreButtonRef }) => (
  <nav className="cc-bottom" aria-label="Workspace tabs">
    {tabs.map(({ id, label, icon: Icon }) => (
      <button
        key={id}
        type="button"
        className="cc-bottom__item"
        aria-current={active === id && !moreOpen ? "page" : undefined}
        onClick={() => onSelect(id)}
      >
        <span className="cc-bottom__pill">
          <Icon aria-hidden="true" />
        </span>
        {label}
      </button>
    ))}
    <button
      type="button"
      ref={moreButtonRef}
      className="cc-bottom__item"
      aria-haspopup="dialog"
      aria-expanded={moreOpen}
      aria-current={moreActive || moreOpen ? "page" : undefined}
      onClick={onToggleMore}
    >
      <span className="cc-bottom__pill">
        <Ellipsis aria-hidden="true" />
      </span>
      More
    </button>
  </nav>
);

export default MobileBottomNav;
